import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import contentConfig from '@/modules/content/config';
import {
  ContentEntity,
  CourseContentType,
  JobEntityStatusEnum,
  PathContentType,
  PerformanceStatusEnum,
  ResourceTypeEnum,
} from '@/types/schema';

import { PARENT_ENTITY_ID } from '../../exercise/views/Details/utils';
import { PathPerformanceFragment } from '../graphql/fragments/generated/PathPerformance';

interface GetContentUrlParams {
  id: string;
  type: ResourceTypeEnum;
}

export const getIsRedirectionDisabled = (jobStatus: JobEntityStatusEnum | undefined) => {
  if (jobStatus) {
    return jobStatus !== JobEntityStatusEnum.Completed;
  }

  return false;
};

export const getContentUrl = (params: GetContentUrlParams) => {
  const { id, type } = params;
  const routes = contentConfig[type].routes;

  return {
    view: routes.details(id),
    manage: routes.manageDetails(id),
    edit: routes.edit?.(id),
  };
};

// if resumeLearningContent exists -> get url
// else get url for first content in first section
// else get url for Path Details
export const getResumeLearningContentUrl = (
  path: PathContentType,
  isAdmin: boolean,
  pathPerformance?: PathPerformanceFragment,
) => {
  let resumeLearningUrl: string | null = null;
  const resumeLearningContent = pathPerformance?.pathResume as ContentEntity;

  if (resumeLearningContent) {
    resumeLearningUrl = isAdmin
      ? getContentUrl(resumeLearningContent).manage
      : getContentUrl(resumeLearningContent).view;
  }

  const sectionContent = path.sections?.[0]?.childResources?.[0];
  if (!resumeLearningUrl && sectionContent) {
    resumeLearningUrl = isAdmin
      ? getContentUrl(sectionContent).manage
      : getContentUrl(sectionContent).view;
  }

  if (resumeLearningUrl) {
    return resumeLearningUrl;
  }

  return contentConfig[ResourceTypeEnum.Path].routes.details(path.id);
};

const LOCK_CONTENT_TYPES = [
  ResourceTypeEnum.Course,
  ResourceTypeEnum.Article,
  ResourceTypeEnum.Path,
  ResourceTypeEnum.ExternalContent,
  ResourceTypeEnum.Scorm,
  ResourceTypeEnum.Quiz,
  ResourceTypeEnum.Checkin,
];

const addDateSuffix = (t: TFunction, date?: string | null) =>
  date
    ? ' ' +
      t('Path:OnDate', {
        date: formatDateWithYear(date, DateFormat.Date),
      })
    : '';

export const getAvailableContentTooltip = (t: TFunction, content: ContentEntity) => {
  const performance = (content as CourseContentType).performance;

  if (LOCK_CONTENT_TYPES.includes(content.type) && performance) {
    const status = performance.status;
    switch (status) {
      case PerformanceStatusEnum.Completed: {
        return `${t('Path:ContentAlreadyComplete')}${addDateSuffix(t, performance.completedAt)}`;
      }
      case PerformanceStatusEnum.NotStarted: {
        return `${t('Path:ContentEnrolled')}${addDateSuffix(
          t,
          performance.enrolmentInfo?.assignedAt,
        )}`;
      }
      case PerformanceStatusEnum.InProgress: {
        return `${t('Path:ContentAlreadyStarted')}${addDateSuffix(
          t,
          performance.enrolmentInfo?.assignedAt,
        )}`;
      }
      default: {
        return '';
      }
    }
  }
  return '';
};

export const getPathSearchParam = (pathId: string) => ({
  [PARENT_ENTITY_ID]: pathId,
});
